import tiktokPixelService from './analytics/TikTokPixelService';
import { supabase } from './supabaseClient';


/**
 * Analytics & Tracking Service
 *
 * Tracks user interactions, package selections, conversions, and other key metrics
 * to help improve the wizard flow and understand user behavior.
 */
export class AnalyticsTrackingService {

  // ... (previous methods)

  /**
   * Track package selection
   */
  static async trackPackageSelection(userId, packageId, country, pathway) {
    try {
      const event = {
        user_id: userId,
        event_type: 'package_selected',
        event_data: {
          package_id: packageId,
          package_name: this.getPackageName(packageId),
          country: country,
          pathway: pathway,
          selected_at: new Date().toISOString()
        }
      };

      await this.logEvent(event);

      // Update package selection metrics
      await this.updatePackageMetrics(packageId, country);

      // Track TikTok Pixel Event
      tiktokPixelService.track('AddToCart', {
        content_id: packageId,
        content_name: this.getPackageName(packageId),
        content_type: 'product',
        currency: 'LKR',
        value: 1 // Or actual value
      });

      return { success: true };
    } catch (error) {
      console.error('Error tracking package selection:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Track wizard completion
   */
  static async trackWizardCompletion(userId, wizardData, selectedPackage, selectedPathways) {
    try {
      const event = {
        user_id: userId,
        event_type: 'wizard_completed',
        event_data: {
          completion_time: new Date().toISOString(),
          selected_package: selectedPackage,
          selected_pathways: selectedPathways,
          profile_summary: this.summarizeProfile(wizardData)
        }
      };

      await this.logEvent(event);

      // Calculate conversion probability
      await this.calculateConversionProbability(userId, wizardData, selectedPackage);

      // Track TikTok Pixel Lead
      tiktokPixelService.trackLead({
        content_name: 'Wizard Completion',
        value: 1
      });

      return { success: true };
    } catch (error) {
      console.error('Error tracking wizard completion:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Track consultation booking
   */
  static async trackConsultationBooking(userId, consultationData) {
    try {
      const event = {
        user_id: userId,
        event_type: 'consultation_booked',
        event_data: {
          consultation_type: consultationData.type,
          package: consultationData.package,
          country: consultationData.country,
          scheduled_time: consultationData.scheduledTime,
          booked_at: new Date().toISOString()
        }
      };

      await this.logEvent(event);

      // Mark conversion milestone
      await this.markConversionMilestone(userId, 'consultation_booked', consultationData);

      // TikTok Pixel Contact
      tiktokPixelService.trackContact('Consultation');

      return { success: true };
    } catch (error) {
      console.error('Error tracking consultation booking:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Track wizard step completion
   */
  static async trackWizardStep(userId, step, data = {}) {
    try {
      const event = {
        user_id: userId,
        event_type: 'wizard_step_completed',
        step_number: step,
        event_data: {
          ...data,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href
        }
      };

      await this.logEvent(event);
      return { success: true };
    } catch (error) {
      console.error('Error tracking wizard step:', error);
      return { success: false, error: error.message };
    }
  }

  // In-memory storage for step timing (keyed by sessionId)
  static stepTimings = new Map();

  /**
   * Track when a wizard step is viewed (start timing)
   * @param {string} sessionId - Unique wizard session ID
   * @param {number} stepNumber - Current step number
   * @param {string} stepName - Name/label of the step
   */
  static trackStepView(sessionId, stepNumber, stepName = '') {
    try {
      const now = Date.now();

      // Store the start time for this step
      if (!this.stepTimings.has(sessionId)) {
        this.stepTimings.set(sessionId, {
          steps: new Map(),
          startedAt: now
        });
      }

      const session = this.stepTimings.get(sessionId);
      session.steps.set(stepNumber, {
        name: stepName,
        viewedAt: now,
        completedAt: null,
        duration: null
      });

      // Log the step view event (async, don't await)
      this.logEvent({
        user_id: sessionId,
        event_type: 'wizard_step_viewed',
        event_data: {
          step_number: stepNumber,
          step_name: stepName,
          timestamp: new Date().toISOString()
        }
      }).catch(console.error);

      return { success: true };
    } catch (error) {
      console.error('Error tracking step view:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Track when a wizard step is completed (end timing)
   * @param {string} sessionId - Unique wizard session ID
   * @param {number} stepNumber - Completed step number
   * @param {Object} stepData - Optional data about the step
   */
  static async trackStepComplete(sessionId, stepNumber, stepData = {}) {
    try {
      const now = Date.now();
      const session = this.stepTimings.get(sessionId);

      let duration = null;
      if (session && session.steps.has(stepNumber)) {
        const stepTiming = session.steps.get(stepNumber);
        stepTiming.completedAt = now;
        duration = now - stepTiming.viewedAt;
        stepTiming.duration = duration;
      }

      // Log the step completion with duration
      await this.logEvent({
        user_id: sessionId,
        event_type: 'wizard_step_completed_with_timing',
        event_data: {
          step_number: stepNumber,
          duration_ms: duration,
          duration_seconds: duration ? Math.round(duration / 1000) : null,
          step_data: stepData,
          timestamp: new Date().toISOString()
        }
      });

      return { success: true, duration };
    } catch (error) {
      console.error('Error tracking step completion:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get total time spent in wizard session
   * @param {string} sessionId - Unique wizard session ID
   */
  static getSessionDuration(sessionId) {
    const session = this.stepTimings.get(sessionId);
    if (!session) return null;
    return Date.now() - session.startedAt;
  }

  /**
   * Get time spent on each step
   * @param {string} sessionId - Unique wizard session ID
   */
  static getStepDurations(sessionId) {
    const session = this.stepTimings.get(sessionId);
    if (!session) return {};

    const durations = {};
    session.steps.forEach((timing, stepNumber) => {
      durations[stepNumber] = {
        name: timing.name,
        duration_ms: timing.duration,
        duration_seconds: timing.duration ? Math.round(timing.duration / 1000) : null,
        completed: !!timing.completedAt
      };
    });
    return durations;
  }

  /**
   * Track wizard exit/abandonment
   * @param {string} sessionId - Unique wizard session ID
   * @param {number} lastStep - Last completed step before exit
   * @param {string} reason - Reason for exit (optional)
   */
  static async trackWizardExit(sessionId, lastStep, reason = 'unknown') {
    try {
      const sessionDuration = this.getSessionDuration(sessionId);
      const stepDurations = this.getStepDurations(sessionId);

      await this.logEvent({
        user_id: sessionId,
        event_type: 'wizard_exited',
        event_data: {
          last_step: lastStep,
          exit_reason: reason,
          session_duration_ms: sessionDuration,
          session_duration_seconds: sessionDuration ? Math.round(sessionDuration / 1000) : null,
          step_durations: stepDurations,
          timestamp: new Date().toISOString()
        }
      });

      // Clean up session data
      this.stepTimings.delete(sessionId);

      return { success: true };
    } catch (error) {
      console.error('Error tracking wizard exit:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Clear session data (call after successful wizard completion)
   * @param {string} sessionId - Unique wizard session ID
   */
  static clearSession(sessionId) {
    this.stepTimings.delete(sessionId);
  }

  /**
   * Track country selection
   */
  static async trackCountrySelection(userId, countries, isMultipleSelection = false) {
    try {
      const event = {
        user_id: userId,
        event_type: 'country_selection',
        event_data: {
          selected_countries: countries,
          is_multiple_selection: isMultipleSelection,
          country_count: countries.length
        }
      };

      await this.logEvent(event);

      // Update country popularity metrics
      await this.updateCountryMetrics(countries);

      return { success: true };
    } catch (error) {
      console.error('Error tracking country selection:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Track pathway recommendation view
   */
  static async trackPathwayRecommendationView(userId, pathways, recommendedPathway) {
    try {
      const event = {
        user_id: userId,
        event_type: 'pathway_recommendation_viewed',
        event_data: {
          available_pathways: pathways.map(p => p.pathway_key || p.id),
          recommended_pathway: recommendedPathway,
          view_timestamp: new Date().toISOString()
        }
      };

      await this.logEvent(event);
      return { success: true };
    } catch (error) {
      console.error('Error tracking pathway recommendation:', error);
      return { success: false, error: error.message };
    }
  }



  /**
   * Track referral generation
   */
  static async trackReferral(userId, referredUserId, packageType, status = 'pending') {
    try {
      const event = {
        user_id: userId,
        event_type: 'referral_generated',
        event_data: {
          referred_user_id: referredUserId,
          package_type: packageType,
          status: status,
          referral_value: this.getReferralValue(packageType),
          created_at: new Date().toISOString()
        }
      };

      await this.logEvent(event);

      // Update referral metrics
      await this.updateReferralMetrics(userId, packageType);

      return { success: true };
    } catch (error) {
      console.error('Error tracking referral:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Track document upload
   */
  static async trackDocumentUpload(userId, documentType, fileSize, success = true) {
    try {
      const event = {
        user_id: userId,
        event_type: 'document_uploaded',
        event_data: {
          document_type: documentType,
          file_size: fileSize,
          success: success,
          uploaded_at: new Date().toISOString()
        }
      };

      await this.logEvent(event);
      return { success: true };
    } catch (error) {
      console.error('Error tracking document upload:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Track scoring results
   */
  static async trackScoringResults(userId, country, pathways, bestMatch) {
    try {
      const event = {
        user_id: userId,
        event_type: 'scoring_completed',
        event_data: {
          country: country,
          pathways_evaluated: pathways.length,
          best_match: bestMatch,
          average_score: this.calculateAverageScore(pathways),
          score_range: this.calculateScoreRange(pathways)
        }
      };

      await this.logEvent(event);
      return { success: true };
    } catch (error) {
      console.error('Error tracking scoring results:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get analytics dashboard data
   */
  static async getDashboardMetrics(dateRange = '30d') {
    try {
      const metrics = {
        // Wizard metrics
        wizard: await this.getWizardMetrics(dateRange),

        // Country popularity
        countries: await this.getCountryMetrics(dateRange),

        // Package selections
        packages: await this.getPackageMetrics(dateRange),

        // Conversion funnel
        conversions: await this.getConversionMetrics(dateRange),

        // Referral metrics
        referrals: await this.getReferralMetrics(dateRange),

        // User engagement
        engagement: await this.getEngagementMetrics(dateRange)
      };

      return metrics;
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      throw error;
    }
  }

  /**
   * Get real-time analytics
   */
  static async getRealtimeMetrics() {
    try {
      const today = new Date().toISOString().split('T')[0];

      const metrics = {
        activeUsers: await this.getActiveUsersCount(today),
        wizardsStarted: await this.getEventCount('wizard_step_completed', today),
        packagesSelected: await this.getEventCount('package_selected', today),
        consultationsBooked: await this.getEventCount('consultation_booked', today),
        topCountries: await this.getTopCountries(today, 5),
        topPackages: await this.getTopPackages(today, 3)
      };

      return metrics;
    } catch (error) {
      console.error('Error fetching realtime metrics:', error);
      throw error;
    }
  }

  /**
   * Generate analytics report
   */
  static async generateReport(type = 'weekly', dateRange) {
    try {
      const report = {
        reportType: type,
        generatedAt: new Date().toISOString(),
        period: dateRange || this.getDefaultDateRange(type),
        metrics: await this.getDashboardMetrics(dateRange),
        insights: await this.generateInsights(dateRange),
        recommendations: await this.generateRecommendations(dateRange)
      };

      return report;
    } catch (error) {
      console.error('Error generating report:', error);
      throw error;
    }
  }

  // ============ HELPER METHODS ============

  /**
   * Log event to Supabase
   */
  static async logEvent(event) {
    if (!supabase) {
      console.warn('Supabase not configured, skipping event logging');
      return;
    }

    const { error } = await supabase
      .from('analytics_events')
      .insert([{
        user_id: event.user_id,
        event_type: event.event_type,
        event_data: event.event_data,
        created_at: new Date().toISOString()
      }]);

    if (error) throw error;
  }

  /**
   * Update country popularity metrics
   */
  static async updateCountryMetrics(countries) {
    if (!supabase) return;

    for (const country of countries) {
      const { error } = await supabase.rpc('increment_country_selection', {
        country_code: country,
        increment_by: 1
      });

      if (error) console.error('Error updating country metrics:', error);
    }
  }

  /**
   * Update package selection metrics
   */
  static async updatePackageMetrics(packageId, country) {
    if (!supabase) return;

    const { error } = await supabase.rpc('increment_package_selection', {
      package_id: packageId,
      country_code: country,
      increment_by: 1
    });

    if (error) console.error('Error updating package metrics:', error);
  }

  /**
   * Calculate conversion probability
   */
  static async calculateConversionProbability(userId, wizardData, selectedPackage) {
    // Implementation for conversion probability calculation
    // This could use ML models in the future
    const probability = this.estimateConversionProbability(wizardData, selectedPackage);

    await this.logEvent({
      user_id: userId,
      event_type: 'conversion_probability_calculated',
      event_data: {
        package: selectedPackage,
        probability: probability,
        factors: this.getConversionFactors(wizardData)
      }
    });

    return probability;
  }

  /**
   * Get package name from ID
   */
  static getPackageName(packageId) {
    const names = {
      'diy': 'Flight Plan™ DIY',
      'plus': 'Flight Plan™ Plus',
      'pro': 'Flight Plan™ Pro'
    };
    return names[packageId] || packageId;
  }

  /**
   * Get referral value
   */
  static getReferralValue(packageType) {
    const values = {
      'plus': 25000,
      'pro': 50000
    };
    return values[packageType] || 0;
  }

  /**
   * Summarize user profile for analytics
   */
  static summarizeProfile(wizardData) {
    return {
      hasEducation: !!wizardData.education,
      hasExperience: !!(wizardData.experience && wizardData.experience.length > 0),
      hasLanguageSkills: !!wizardData.languageSkills,
      hasFinancialInfo: !!wizardData.financialInfo,
      countriesSelected: wizardData.countrySelection ? 1 : 0,
      profileCompleteness: this.calculateProfileCompleteness(wizardData)
    };
  }

  /**
   * Calculate profile completeness
   */
  static calculateProfileCompleteness(wizardData) {
    const requiredFields = ['personalInfo', 'education', 'languageSkills', 'financialInfo'];
    const completed = requiredFields.filter(field => wizardData[field]).length;
    return (completed / requiredFields.length) * 100;
  }

  /**
   * Calculate average score from pathways
   */
  static calculateAverageScore(pathways) {
    if (!pathways || pathways.length === 0) return 0;
    const total = pathways.reduce((sum, p) => sum + (p.percentageScore || 0), 0);
    return Math.round(total / pathways.length);
  }

  /**
   * Calculate score range
   */
  static calculateScoreRange(pathways) {
    if (!pathways || pathways.length === 0) return { min: 0, max: 0 };
    const scores = pathways.map(p => p.percentageScore || 0);
    return {
      min: Math.min(...scores),
      max: Math.max(...scores)
    };
  }

  /**
   * Estimate conversion probability (simplified model)
   */
  static estimateConversionProbability(wizardData, selectedPackage) {
    let probability = 50; // Base probability

    // Package type adjustment
    if (selectedPackage === 'pro') probability += 20;
    else if (selectedPackage === 'plus') probability += 10;

    // Profile completeness
    const completeness = this.calculateProfileCompleteness(wizardData);
    probability += (completeness / 100) * 20;

    // Country selection
    const countries = wizardData.countrySelection?.selectedCountries || [];
    if (countries.length > 1) probability += 10;

    return Math.min(Math.max(probability, 0), 95);
  }

  /**
   * Get conversion factors
   */
  static getConversionFactors(wizardData) {
    return {
      profileComplete: this.calculateProfileCompleteness(wizardData) >= 80,
      hasExperience: !!(wizardData.experience && wizardData.experience.length > 0),
      hasLanguageScore: !!(wizardData.languageSkills && wizardData.languageSkills.ieltsScore),
      hasFinancialProof: !!(wizardData.financialInfo && wizardData.financialInfo.fundsAvailable)
    };
  }

  /**
   * Mark conversion milestone
   */
  static async markConversionMilestone(userId, milestone, data) {
    // Track conversion funnel progression
    await this.logEvent({
      user_id: userId,
      event_type: 'conversion_milestone',
      event_data: {
        milestone: milestone,
        data: data,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Get default date range for reports
   */
  static getDefaultDateRange(type) {
    const now = new Date();
    let startDate;

    switch (type) {
      case 'daily':
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case 'weekly':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'monthly':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'quarterly':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      default:
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    }

    return {
      start: startDate.toISOString(),
      end: now.toISOString()
    };
  }

  // ============ DATABASE QUERY METHODS ============

  static async getWizardMetrics(dateRange) {
    // Implementation for wizard metrics
    return {
      totalCompletions: 0,
      averageTime: 0,
      dropOffPoints: [],
      completionRate: 0
    };
  }

  static async getCountryMetrics(dateRange) {
    // Implementation for country metrics
    return {
      totalSelections: 0,
      byCountry: {},
      trend: []
    };
  }

  static async getPackageMetrics(dateRange) {
    // Implementation for package metrics
    return {
      totalSelections: 0,
      byPackage: {
        diy: 0,
        plus: 0,
        pro: 0
      },
      conversionRate: 0
    };
  }

  static async getConversionMetrics(dateRange) {
    // Implementation for conversion metrics
    return {
      wizardToConsultation: 0,
      consultationToClient: 0,
      overallConversion: 0,
      funnel: []
    };
  }

  static async getReferralMetrics(dateRange) {
    // Implementation for referral metrics
    return {
      totalReferrals: 0,
      successfulReferrals: 0,
      totalValue: 0,
      topReferrers: []
    };
  }

  static async getEngagementMetrics(dateRange) {
    // Implementation for engagement metrics
    return {
      activeUsers: 0,
      averageSessionTime: 0,
      pageViews: 0,
      bounceRate: 0
    };
  }

  static async getActiveUsersCount(date) {
    // Get count of unique users active today
    return 0;
  }

  static async getEventCount(eventType, date) {
    // Get count of specific event type on date
    return 0;
  }

  static async getTopCountries(date, limit) {
    // Get top countries by selection count
    return [];
  }

  static async getTopPackages(date, limit) {
    // Get top packages by selection count
    return [];
  }

  static async generateInsights(dateRange) {
    // Generate automated insights from data
    return [
      'PRO package has highest conversion rate',
      'Canada is most popular destination',
      'Users with complete profiles convert 40% more'
    ];
  }

  static async generateRecommendations(dateRange) {
    // Generate data-driven recommendations
    return [
      'Promote PRO package to users selecting multiple countries',
      'Create specific content for Canada pathway',
      'Implement profile completion incentives'
    ];
  }
}

export default AnalyticsTrackingService;
